import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import Chip from "@mui/material/Chip";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { useQuery } from "@apollo/client/react";

import { ASSETS_BY_IDS_WITH_QUANTITY_QUERY } from "../../../constants/graphql/queries";
import { ADD_INTERNAL_WORK_ORDER_ITEM_MUTATION } from "../../../constants/graphql/mutations";
import useMutation from "../../../hooks/useMutation";
import useNotification from "../../../hooks/notification";
import BulkAddToCartTable from "./BulkAddToCartTable";

export default function BulkAddToInternalWorkOrderProgressModal({
  selectedIds,
  internalWorkOrderId,
  selectedWorkOrder,
  onClose,
  onComplete,
}) {
  const { notifySuccess, notifyError } = useNotification();
  const [addInternalWorkOrderItem] = useMutation(ADD_INTERNAL_WORK_ORDER_ITEM_MUTATION);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, currentAsset: "" });
  const [assetStatus, setAssetStatus] = useState({});
  const [quantityOverrides, setQuantityOverrides] = useState({});

  const handleQuantityChange = (assetId, qty) =>
    setQuantityOverrides(prev => ({ ...prev, [assetId]: qty }));

  const getEffectiveQty = asset =>
    quantityOverrides[asset.id] !== undefined ? quantityOverrides[asset.id] : asset.availableQuantity;

  const { loading: assetsLoading, data: assetsData } = useQuery(ASSETS_BY_IDS_WITH_QUANTITY_QUERY, {
    variables: { assetIds: selectedIds },
    skip: !selectedIds || selectedIds.length === 0,
    fetchPolicy: "network-only",
  });

  const rawAssets = assetsData?.assetsCollection?.collection || [];
  const allocatingAssets = rawAssets.filter(asset => asset.availableQuantity > 0);
  const nonAllocatingAssets = rawAssets.filter(asset => asset.availableQuantity <= 0);
  const orderNumber =
    selectedWorkOrder?.revisionNumber > 0
      ? `${selectedWorkOrder.internalWorkOrderNumber}.R${selectedWorkOrder.revisionNumber}`
      : selectedWorkOrder?.internalWorkOrderNumber;
  const projectName = selectedWorkOrder?.project?.name;
  const orderDetails = [orderNumber && `Internal Order #: ${orderNumber}`, projectName]
    .filter(Boolean)
    .join(", ");

  const handleAddToInternalWorkOrder = async () => {
    if (!rawAssets.length) {
      notifyError("No assets found");
      onClose();
      return;
    }

    setProcessing(true);
    setProgress({ current: 0, total: rawAssets.length, currentAsset: "" });
    setAssetStatus({});

    const failed = [];
    for (let index = 0; index < rawAssets.length; index++) {
      const asset = rawAssets[index];
      const isNonAllocating = asset.availableQuantity <= 0;
      setAssetStatus(prev => ({ ...prev, [asset.id]: "moving" }));
      setProgress({
        current: index + 1,
        total: rawAssets.length,
        currentAsset: asset.assetId || asset.itemId || asset.id,
      });

      const qty = isNonAllocating ? 0 : getEffectiveQty(asset);
      if (!isNonAllocating && (!qty || qty <= 0)) {
        setAssetStatus(prev => ({ ...prev, [asset.id]: "failed" }));
        failed.push({ assetId: asset.assetId || asset.id, error: "No available quantity" });
        continue;
      }

      try {
        await addInternalWorkOrderItem({
          variables: {
            internalWorkOrderId,
            input: {
              assetId: asset.id,
              quantity: qty,
              nonAllocating: isNonAllocating,
            },
          },
        });
        setAssetStatus(prev => ({ ...prev, [asset.id]: "moved" }));
      } catch (mutationError) {
        setAssetStatus(prev => ({ ...prev, [asset.id]: "failed" }));
        failed.push({
          assetId: asset.assetId || asset.id,
          error: mutationError.message || "Unknown error",
        });
      }
    }

    setProcessing(false);
    if (failed.length === 0) {
      notifySuccess(`Successfully added ${rawAssets.length} assets to the internal work order`);
      onComplete();
      onClose();
    } else {
      const successCount = rawAssets.length - failed.length;
      if (successCount > 0) {
        notifySuccess(`Added ${successCount} assets. ${failed.length} failed.`);
        onComplete();
      } else {
        notifyError("Failed to add any assets to the internal work order");
      }
    }
  };

  const totalAssets = rawAssets.length || selectedIds.length;

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "center" }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography fontSize={20} fontWeight={500} color="textPrimary">
              Bulk Add to Internal Work Order
            </Typography>
            <Chip label={totalAssets} size="small" color="primary" variant="outlined" />
          </Box>
          {orderDetails && (
            <Typography variant="body2" color="text.secondary" align="center">
              {orderDetails}
            </Typography>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {assetsLoading && (
          <Box display="flex" flexDirection="column" alignItems="center" py={3}>
            <LinearProgress sx={{ width: "100%", mb: 2 }} />
            <Typography variant="body2" color="textSecondary">
              Fetching asset data for {totalAssets} selected assets...
            </Typography>
          </Box>
        )}
        {!assetsLoading && allocatingAssets.length > 0 && (
          <BulkAddToCartTable
            assets={allocatingAssets}
            statusMap={assetStatus}
            quantityOverrides={quantityOverrides}
            onQuantityChange={handleQuantityChange}
            processing={processing}
          />
        )}
        {!assetsLoading && nonAllocatingAssets.length > 0 && (
          <Box py={2} px={1} mt={allocatingAssets.length > 0 ? 2 : 0}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1, fontWeight: 500 }}>
              {nonAllocatingAssets.length === 1
                ? "1 item with no available quantity will be added as Non-allocating:"
                : `${nonAllocatingAssets.length} items with no available quantity will be added as Non-allocating:`}
            </Typography>
            <List dense>
              {nonAllocatingAssets.map(asset => (
                <ListItem key={asset.id} divider>
                  <ListItemText
                    primary={asset.itemId || asset.assetId}
                    secondary={asset.description || "Non-allocating"}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>
      {processing && (
        <LinearProgress variant="determinate" value={(progress.current / progress.total) * 100} />
      )}
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {!assetsLoading && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddToInternalWorkOrder}
            disabled={processing}
          >
            Add to Internal Work Order
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
